import React, { useEffect, useMemo, useState, useRef, Fragment, createElement } from 'react';
import { BarChart3, Users, DollarSign, ChevronDown, ChevronRight, Search, AlertCircle, ShoppingBag, Download, FileSpreadsheet, TrendingUp, Building, ChevronUp, Calendar, SlidersHorizontal, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../contexts/OrderContext';
import { Card } from '../components/ui/Card';
import { SearchBar } from '../components/ui/SearchBar';
import { DatePicker } from '../components/ui/DatePicker';
import { Button } from '../components/ui/Button';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { displayProductName, formatCurrency } from '../utils/formatters';
type QuickFilter = 'Hoy' | 'Esta Semana' | 'Este Mes' | 'Personalizado' | 'Todo';
export function CompanyConsumptionPage() {
  useDocumentTitle('Reporte de Consumos');
  const {
    user
  } = useAuth();
  const {
    orders
  } = useOrders();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('Este Mes');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateError, setDateError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Date validation
  const validateDates = (from: string, to: string) => {
    if (!from || !to) {
      setDateError('');
      return true;
    }
    if (new Date(from) > new Date(to)) {
      setDateError('La fecha "Desde" no puede ser posterior a "Hasta"');
      return false;
    }
    setDateError('');
    return true;
  };
  const handleDateFromChange = (v: string) => {
    setDateFrom(v);
    if (v || dateTo) setQuickFilter('Personalizado');
    validateDates(v, dateTo);
  };
  const handleDateToChange = (v: string) => {
    setDateTo(v);
    if (dateFrom || v) setQuickFilter('Personalizado');
    validateDates(dateFrom, v);
  };
  const handleQuickFilter = (f: QuickFilter) => {
    setQuickFilter(f);
    if (f !== 'Personalizado') {
      setDateFrom('');
      setDateTo('');
      setDateError('');
    }
  };
  const clearFilters = () => {
    setQuickFilter('Este Mes');
    setDateFrom('');
    setDateTo('');
    setDateError('');
    setSearchTerm('');
  };
  // Access Control
  if (!user?.permissions?.isManager) {
    return <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center" data-id="element-1981">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-1982">
          <AlertCircle size={32} className="text-red-600" data-id="element-1983" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1984">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 max-w-md" data-id="element-1985">
          No tienes permisos para ver esta sección. Si eres gerente de la
          empresa, solicita acceso al administrador.
        </p>
      </div>;
  }
  // Filter Orders
  const filteredOrders = useMemo(() => {
    if (!user?.company) return [];
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = now.getDay();
    const mondayDiff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayDiff);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return orders.filter(order => {
      if (order.company !== user.company) return false;
      if (order.status === 'Cancelado') return false;
      const orderDate = new Date(order.orderDateISO || order.orderDate);
      if (quickFilter === 'Personalizado') {
        // Custom date range
        if (dateFrom || dateTo) {
          try {
            const cleanDate = order.orderDate.replace(',', '');
            const dateTimeParts = cleanDate.split(' ');
            const datePart = dateTimeParts[0];
            const parts = datePart.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0]);
              const month = parseInt(parts[1]);
              const year = parseInt(parts[2]);
              const orderDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              if (dateFrom && orderDateStr < dateFrom) return false;
              if (dateTo && orderDateStr > dateTo) return false;
            } else {
              // Fallback: use orderDateISO
              if (order.orderDateISO) {
                const isoDate = order.orderDateISO.split('T')[0];
                if (dateFrom && isoDate < dateFrom) return false;
                if (dateTo && isoDate > dateTo) return false;
              }
            }
          } catch {
            return true;
          }
        }
        return true;
      }
      switch (quickFilter) {
        case 'Hoy':
          return orderDate >= startOfDay;
        case 'Esta Semana':
          return orderDate >= startOfWeek;
        case 'Este Mes':
          return orderDate >= startOfMonth;
        default:
          return true;
      }
    });
  }, [orders, user?.company, quickFilter, dateFrom, dateTo]);
  // Group by Employee
  const employeeStats = useMemo(() => {
    const stats: Record<string, {
      name: string;
      orders: typeof filteredOrders;
      totalSpent: number;
      lastOrderDate: Date;
      branches: Record<string, number>;
    }> = {};
    filteredOrders.forEach(order => {
      const key = order.name;
      if (!stats[key]) {
        stats[key] = {
          name: order.name,
          orders: [],
          totalSpent: 0,
          lastOrderDate: new Date(0),
          branches: {}
        };
      }
      const orderTotal = parseFloat(order.total) || 0;
      const orderDate = new Date(order.orderDateISO || order.orderDate);
      stats[key].orders.push(order);
      stats[key].totalSpent += orderTotal;
      if (orderDate > stats[key].lastOrderDate) stats[key].lastOrderDate = orderDate;
      const branch = order.branch || 'Principal';
      stats[key].branches[branch] = (stats[key].branches[branch] || 0) + 1;
    });
    let result = Object.values(stats).map(stat => {
      const mostFrequentBranch = Object.entries(stat.branches).sort((a, b) => b[1] - a[1])[0]?.[0];
      return {
        ...stat,
        primaryBranch: mostFrequentBranch || 'N/A'
      };
    }).sort((a, b) => b.totalSpent - a.totalSpent);
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter(stat => stat.name.toLowerCase().includes(lowerTerm));
    }
    return result;
  }, [filteredOrders, searchTerm]);
  // Summary Metrics
  const {
    summary,
    maxConsumption
  } = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const totalConsumption = filteredOrders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
    const activeEmployees = employeeStats.length;
    const avgPerEmployee = activeEmployees > 0 ? totalConsumption / activeEmployees : 0;
    const max = employeeStats.length > 0 ? employeeStats[0].totalSpent : 0;
    return {
      summary: {
        totalOrders,
        totalConsumption,
        activeEmployees,
        avgPerEmployee
      },
      maxConsumption: max
    };
  }, [filteredOrders, employeeStats]);
  const toggleExpand = (employeeName: string) => {
    setExpandedEmployee(expandedEmployee === employeeName ? null : employeeName);
  };
  // Get active date label for display & export
  const getDateLabel = () => {
    if (quickFilter === 'Personalizado') {
      if (dateFrom && dateTo) return `${dateFrom} al ${dateTo}`;
      if (dateFrom) return `Desde ${dateFrom}`;
      if (dateTo) return `Hasta ${dateTo}`;
      return 'Personalizado';
    }
    return quickFilter === 'Todo' ? 'Histórico' : quickFilter;
  };
  const hasActiveFilters = quickFilter !== 'Este Mes' || searchTerm || dateFrom || dateTo;
  // Professional Excel Export
  const exportExcel = (type: 'summary' | 'detailed') => {
    const dateLabel = getDateLabel();
    const today = new Date().toLocaleDateString('es-DO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const esc = (s: string | number) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const bThin = (color = '#E5E7EB') => `<Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="${color}"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="${color}"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="${color}"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="${color}"/>`;
    const styles = `<Styles>
      <Style ss:ID="Default"><Alignment ss:Vertical="Center" ss:WrapText="0"/><Font ss:FontName="Calibri" ss:Size="10"/></Style>
      <Style ss:ID="banner"><Interior ss:Color="#111827" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="18" ss:Bold="1" ss:Color="#FFFFFF"/><Alignment ss:Vertical="Center" ss:Horizontal="Left"/><Borders>${bThin('#111827')}</Borders></Style>
      <Style ss:ID="bannerSub"><Interior ss:Color="#1F2937" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#D1D5DB"/><Alignment ss:Vertical="Center" ss:Horizontal="Left"/><Borders>${bThin('#1F2937')}</Borders></Style>
      <Style ss:ID="bannerSubRight"><Interior ss:Color="#1F2937" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin('#1F2937')}</Borders></Style>
      <Style ss:ID="kpiLabel"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="8" ss:Color="#6B7280" ss:Bold="1"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#E5E7EB')}</Borders></Style>
      <Style ss:ID="kpiValue"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="14" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#E5E7EB')}</Borders></Style>
      <Style ss:ID="kpiValueGreen"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="14" ss:Bold="1" ss:Color="#059669"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#E5E7EB')}</Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="spacer"><Font ss:Size="4"/></Style>
      <Style ss:ID="thC"><Interior ss:Color="#1F2937" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#374151')}</Borders></Style>
      <Style ss:ID="thL"><Interior ss:Color="#1F2937" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/><Alignment ss:Vertical="Center" ss:Horizontal="Left" ss:Indent="1"/><Borders>${bThin('#374151')}</Borders></Style>
      <Style ss:ID="thR"><Interior ss:Color="#1F2937" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#FFFFFF"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin('#374151')}</Borders></Style>
      <Style ss:ID="dE"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dEB"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dEC"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dER"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin()}</Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="dEBR"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin()}</Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="dERank"><Interior ss:Color="#FFFFFF" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#9CA3AF"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dO"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dOB"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dOC"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="dOR"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Color="#374151"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin()}</Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="dOBR"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders>${bThin()}</Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="dORank"><Interior ss:Color="#F9FAFB" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#9CA3AF"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin()}</Borders></Style>
      <Style ss:ID="stGreenE"><Interior ss:Color="#D1FAE5" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#065F46"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#A7F3D0')}</Borders></Style>
      <Style ss:ID="stGreenO"><Interior ss:Color="#D1FAE5" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#065F46"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#A7F3D0')}</Borders></Style>
      <Style ss:ID="stBlueE"><Interior ss:Color="#DBEAFE" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#1E40AF"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#BFDBFE')}</Borders></Style>
      <Style ss:ID="stBlueO"><Interior ss:Color="#DBEAFE" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#1E40AF"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#BFDBFE')}</Borders></Style>
      <Style ss:ID="stYellowE"><Interior ss:Color="#FEF3C7" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#92400E"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#FDE68A')}</Borders></Style>
      <Style ss:ID="stYellowO"><Interior ss:Color="#FEF3C7" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#92400E"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#FDE68A')}</Borders></Style>
      <Style ss:ID="stRedE"><Interior ss:Color="#FEE2E2" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#991B1B"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#FECACA')}</Borders></Style>
      <Style ss:ID="stRedO"><Interior ss:Color="#FEE2E2" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="9" ss:Bold="1" ss:Color="#991B1B"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders>${bThin('#FECACA')}</Borders></Style>
      <Style ss:ID="tot"><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center"/><Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/></Borders></Style>
      <Style ss:ID="totR"><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/></Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="totC"><Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="11" ss:Bold="1" ss:Color="#111827"/><Alignment ss:Vertical="Center" ss:Horizontal="Center"/><Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/></Borders></Style>
      <Style ss:ID="totGreen"><Interior ss:Color="#ECFDF5" ss:Pattern="Solid"/><Font ss:FontName="Calibri" ss:Size="12" ss:Bold="1" ss:Color="#059669"/><Alignment ss:Vertical="Center" ss:Horizontal="Right"/><Borders><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#6B7280"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#D1D5DB"/></Borders><NumberFormat ss:Format="#,##0.00"/></Style>
      <Style ss:ID="footer"><Font ss:FontName="Calibri" ss:Size="8" ss:Color="#9CA3AF" ss:Italic="1"/><Alignment ss:Vertical="Center"/></Style>
    </Styles>`;
    const c = (v: string | number, s: string, num = false) => num ? `<Cell ss:StyleID="${s}"><Data ss:Type="Number">${v}</Data></Cell>` : `<Cell ss:StyleID="${s}"><Data ss:Type="String">${esc(v)}</Data></Cell>`;
    let sheetName = '',
      filename = '',
      colsXml = '',
      rowsXml = '',
      freezeRow = 0;
    const numCols = type === 'summary' ? 7 : 8;
    const mergeAcross = numCols - 1;
    const companyName = user?.company || 'Cricket';
    const reportTitle = type === 'summary' ? 'Reporte de Consumos por Empleado' : 'Detalle de Pedidos';
    rowsXml += `<Row ss:Height="36"><Cell ss:StyleID="banner" ss:MergeAcross="${mergeAcross}"><Data ss:Type="String">  ${esc(companyName)}  —  ${esc(reportTitle)}</Data></Cell></Row>`;
    rowsXml += `<Row ss:Height="24"><Cell ss:StyleID="bannerSub" ss:MergeAcross="${Math.max(0, mergeAcross - 2)}"><Data ss:Type="String">  Período: ${esc(dateLabel)}  •  Generado: ${esc(today)}</Data></Cell>`;
    for (let i = 0; i < Math.min(2, mergeAcross); i++) {
      rowsXml += i === Math.min(1, mergeAcross - 1) ? `<Cell ss:StyleID="bannerSubRight"><Data ss:Type="String">Cricket</Data></Cell>` : `<Cell ss:StyleID="bannerSub"><Data ss:Type="String"></Data></Cell>`;
    }
    rowsXml += `</Row>`;
    if (type === 'summary') {
      sheetName = 'Resumen Consumos';
      filename = `Consumos_${companyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xls`;
      colsXml = `<Column ss:Width="42"/><Column ss:Width="190"/><Column ss:Width="130"/><Column ss:Width="75"/><Column ss:Width="145"/><Column ss:Width="120"/><Column ss:Width="115"/>`;
      rowsXml += `<Row ss:Height="8"><Cell ss:StyleID="spacer"/></Row>`;
      rowsXml += `<Row ss:Height="18">${c('TOTAL PEDIDOS', 'kpiLabel')}${c('', 'kpiLabel')}${c('CONSUMO TOTAL (RD$)', 'kpiLabel')}${c('', 'kpiLabel')}${c('EMPLEADOS ACTIVOS', 'kpiLabel')}${c('', 'kpiLabel')}${c('PROMEDIO / EMP.', 'kpiLabel')}</Row>`;
      rowsXml += `<Row ss:Height="30">${c(summary.totalOrders, 'kpiValue', true)}${c('', 'kpiValue')}${c(Math.round(summary.totalConsumption * 100) / 100, 'kpiValueGreen', true)}${c('', 'kpiValue')}${c(summary.activeEmployees, 'kpiValue', true)}${c('', 'kpiValue')}${c(Math.round(summary.avgPerEmployee * 100) / 100, 'kpiValueGreen', true)}</Row>`;
      rowsXml += `<Row ss:Height="8"><Cell ss:StyleID="spacer"/></Row>`;
      freezeRow = 7;
      rowsXml += `<Row ss:Height="30">${c('#', 'thC')}${c('Empleado', 'thL')}${c('Sucursal', 'thL')}${c('Pedidos', 'thC')}${c('Total Consumo (RD$)', 'thR')}${c('Promedio (RD$)', 'thR')}${c('Último Pedido', 'thC')}</Row>`;
      employeeStats.forEach((stat, i) => {
        const even = i % 2 === 0;
        const avg = stat.totalSpent / stat.orders.length;
        const p = (s: string) => even ? 'dE' + s : 'dO' + s;
        rowsXml += `<Row ss:Height="24">${c(i + 1, even ? 'dERank' : 'dORank', true)}${c(stat.name, p('B'))}${c(stat.primaryBranch, p(''))}${c(stat.orders.length, p('C'), true)}${c(Math.round(stat.totalSpent * 100) / 100, p('BR'), true)}${c(Math.round(avg * 100) / 100, p('R'), true)}${c(stat.lastOrderDate.toLocaleDateString('es-DO', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }), p('C'))}</Row>`;
      });
      rowsXml += `<Row ss:Height="4"><Cell ss:StyleID="spacer"/></Row>`;
      rowsXml += `<Row ss:Height="32">${c('', 'tot')}${c('TOTAL GENERAL', 'tot')}${c(employeeStats.length + ' empleados', 'tot')}${c(summary.totalOrders, 'totC', true)}${c(Math.round(summary.totalConsumption * 100) / 100, 'totGreen', true)}${c(Math.round(summary.avgPerEmployee * 100) / 100, 'totR', true)}${c('', 'tot')}</Row>`;
    } else {
      sheetName = 'Detalle Pedidos';
      filename = `Pedidos_${companyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xls`;
      colsXml = `<Column ss:Width="85"/><Column ss:Width="100"/><Column ss:Width="60"/><Column ss:Width="190"/><Column ss:Width="130"/><Column ss:Width="260"/><Column ss:Width="95"/><Column ss:Width="115"/>`;
      const allOrders = [...filteredOrders].sort((a, b) => new Date(b.orderDateISO || b.orderDate).getTime() - new Date(a.orderDateISO || a.orderDate).getTime());
      rowsXml += `<Row ss:Height="8"><Cell ss:StyleID="spacer"/></Row>`;
      rowsXml += `<Row ss:Height="18">${c('TOTAL PEDIDOS', 'kpiLabel')}${c('', 'kpiLabel')}${c('CONSUMO TOTAL (RD$)', 'kpiLabel')}${c('', 'kpiLabel')}${c('EMPLEADOS', 'kpiLabel')}${c('', 'kpiLabel')}${c('PERÍODO', 'kpiLabel')}${c('', 'kpiLabel')}</Row>`;
      rowsXml += `<Row ss:Height="30">${c(allOrders.length, 'kpiValue', true)}${c('', 'kpiValue')}${c(Math.round(summary.totalConsumption * 100) / 100, 'kpiValueGreen', true)}${c('', 'kpiValue')}${c(summary.activeEmployees, 'kpiValue', true)}${c('', 'kpiValue')}${c(dateLabel, 'kpiValue')}${c('', 'kpiValue')}</Row>`;
      rowsXml += `<Row ss:Height="8"><Cell ss:StyleID="spacer"/></Row>`;
      freezeRow = 7;
      rowsXml += `<Row ss:Height="30">${c('No. Orden', 'thL')}${c('Fecha', 'thL')}${c('Hora', 'thC')}${c('Empleado', 'thL')}${c('Sucursal', 'thL')}${c('Producto', 'thL')}${c('Estado', 'thC')}${c('Total (RD$)', 'thR')}</Row>`;
      allOrders.forEach((order, i) => {
        const even = i % 2 === 0;
        const dateObj = new Date(order.orderDateISO || order.orderDate);
        const p = (s: string) => even ? 'dE' + s : 'dO' + s;
        let stStyle = even ? 'stYellowE' : 'stYellowO';
        if (order.status === 'Entregado') stStyle = even ? 'stGreenE' : 'stGreenO';else if (order.status === 'Listo') stStyle = even ? 'stBlueE' : 'stBlueO';else if (order.status === 'Cancelado') stStyle = even ? 'stRedE' : 'stRedO';else if (order.status === 'En Preparación') stStyle = even ? 'stBlueE' : 'stBlueO';
        rowsXml += `<Row ss:Height="24">${c(order.orderNo, p(''))}${c(dateObj.toLocaleDateString('es-DO'), p(''))}${c(dateObj.toLocaleTimeString('es-DO', {
          hour: '2-digit',
          minute: '2-digit'
        }), p('C'))}${c(order.name, p('B'))}${c(order.branch || 'Principal', p(''))}${c(displayProductName(order.product), p(''))}${c(order.status, stStyle)}${c(Math.round(parseFloat(order.total) * 100) / 100, p('BR'), true)}</Row>`;
      });
      rowsXml += `<Row ss:Height="4"><Cell ss:StyleID="spacer"/></Row>`;
      rowsXml += `<Row ss:Height="32">${c('', 'tot')}${c('', 'tot')}${c('', 'tot')}${c('', 'tot')}${c('', 'tot')}${c('', 'tot')}${c('TOTAL GENERAL', 'tot')}${c(Math.round(summary.totalConsumption * 100) / 100, 'totGreen', true)}</Row>`;
    }
    rowsXml += `<Row ss:Height="8"><Cell ss:StyleID="spacer"/></Row>`;
    rowsXml += `<Row ss:Height="16"><Cell ss:StyleID="footer" ss:MergeAcross="${mergeAcross}"><Data ss:Type="String">Generado por Cricket • ${esc(today)} • Este documento es confidencial</Data></Cell></Row>`;
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office"><Title>${esc(sheetName)}</Title><Author>Cricket</Author><Company>${esc(companyName)}</Company><Created>${new Date().toISOString()}</Created></DocumentProperties>
 <ExcelWorkbook xmlns="urn:schemas-microsoft-com:office:excel"><WindowHeight>12000</WindowHeight><WindowWidth>18000</WindowWidth></ExcelWorkbook>
 ${styles}
 <Worksheet ss:Name="${esc(sheetName)}">
  <Table ss:DefaultRowHeight="20">${colsXml}${rowsXml}</Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <PageSetup><Layout x:Orientation="Landscape"/><PageMargins x:Bottom="0.5" x:Left="0.5" x:Right="0.5" x:Top="0.5"/></PageSetup>
   <FitToPage/><Print><FitHeight>0</FitHeight><FitWidth>1</FitWidth></Print>
   <Selected/><FreezePanes/><FrozenNoSplit/><SplitHorizontal>${freezeRow}</SplitHorizontal><TopRowBottomPane>${freezeRow}</TopRowBottomPane><ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;
    const blob = new Blob([xml], {
      type: 'application/vnd.ms-excel'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };
  const quickFilters: {
    key: QuickFilter;
    label: string;
  }[] = [{
    key: 'Hoy',
    label: 'Hoy'
  }, {
    key: 'Esta Semana',
    label: 'Semana'
  }, {
    key: 'Este Mes',
    label: 'Mes'
  }, {
    key: 'Todo',
    label: 'Todo'
  }];
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto space-y-6" data-id="element-1986">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1987">
        <div className="min-w-0" data-id="element-1988">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1989">
            Reporte de Consumos
          </h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-1990">
            {user.company} • Resumen de gastos por empleado
          </p>
        </div>
        {/* Export Dropdown */}
        <div className="relative" ref={exportMenuRef} data-id="element-1991">
          <Button variant="outline" size="md" leftIcon={<Download size={16} data-id="element-1993" />} rightIcon={showExportMenu ? <ChevronUp size={14} data-id="element-1994" /> : <ChevronDown size={14} data-id="element-1995" />} onClick={() => setShowExportMenu(!showExportMenu)} data-id="element-1992">
            Exportar Excel
          </Button>
          {showExportMenu && <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden" data-id="element-1996">
              <button onClick={() => exportExcel('summary')} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors" data-id="element-1997">
                <BarChart3 size={16} className="text-emerald-500 shrink-0" data-id="element-1998" />
                <div data-id="element-1999">
                  <div className="font-medium" data-id="element-2000">Resumen por Empleado</div>
                  <div className="text-xs text-gray-400" data-id="element-2001">Totales agrupados</div>
                </div>
              </button>
              <div className="h-px bg-gray-100" data-id="element-2002" />
              <button onClick={() => exportExcel('detailed')} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors" data-id="element-2003">
                <FileSpreadsheet size={16} className="text-blue-500 shrink-0" data-id="element-2004" />
                <div data-id="element-2005">
                  <div className="font-medium" data-id="element-2006">Detalle Completo</div>
                  <div className="text-xs text-gray-400" data-id="element-2007">
                    Cada pedido individual
                  </div>
                </div>
              </button>
            </div>}
        </div>
      </div>

      {/* Filters Card */}
      <Card className="p-0" noPadding data-id="element-2008">
        {/* Mobile: compact header with toggle */}
        <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between cursor-pointer md:cursor-default" onClick={() => setFiltersOpen(f => !f)} data-id="element-2009">
          <div className="flex items-center gap-2" data-id="element-2010">
            <SlidersHorizontal size={14} className="text-gray-400" data-id="element-2011" />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2012">
              Filtros
            </span>
            {hasActiveFilters && <span className="inline-flex items-center justify-center w-4 h-4 bg-gray-900 text-white rounded-full text-[9px] font-bold" data-id="element-2013">
                {[quickFilter !== 'Este Mes', !!searchTerm, !!dateFrom || !!dateTo].filter(Boolean).length}
              </span>}
          </div>
          <div className="flex items-center gap-2 md:hidden" data-id="element-2014">
            {/* Active filter summary pill */}
            <span className="text-xs text-gray-500 font-medium" data-id="element-2015">
              {getDateLabel()}
            </span>
            {filtersOpen ? <ChevronUp size={16} className="text-gray-400" data-id="element-2016" /> : <ChevronDown size={16} className="text-gray-400" data-id="element-2017" />}
          </div>
        </div>

        {/* Filter body — always visible on md+, collapsible on mobile */}
        <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`} data-id="element-2018">
          <div className="p-4 md:p-5 space-y-4" data-id="element-2019">
            {dateError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-id="element-2020">
                <AlertCircle className="text-red-600 shrink-0 mt-0.5" size={16} data-id="element-2021" />
                <p className="text-sm text-red-700" data-id="element-2022">{dateError}</p>
              </div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-id="element-2023">
              {/* Quick filters */}
              <div className="sm:col-span-2 lg:col-span-1" data-id="element-2024">
                <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider" data-id="element-2025">
                  Período
                </label>
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-id="element-2026">
                  {quickFilters.map(f => <button key={f.key} onClick={() => handleQuickFilter(f.key)} className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md transition-all whitespace-nowrap ${quickFilter === f.key && quickFilter !== 'Personalizado' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2027">
                      {f.label}
                    </button>)}
                </div>
              </div>

              {/* Date From + To side by side on mobile */}
              <div className="grid grid-cols-2 gap-2 sm:contents" data-id="element-2028">
                <div data-id="element-2029">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider" data-id="element-2030">
                    Desde
                  </label>
                  <DatePicker value={dateFrom} onChange={handleDateFromChange} placeholder="Inicio" data-id="element-2031" />
                </div>
                <div data-id="element-2032">
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider" data-id="element-2033">
                    Hasta
                  </label>
                  <DatePicker value={dateTo} onChange={handleDateToChange} placeholder="Fin" data-id="element-2034" />
                </div>
              </div>

              {/* Search */}
              <div className="sm:col-span-2 lg:col-span-2" data-id="element-2035">
                <label className="block text-[11px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider" data-id="element-2036">
                  Buscar empleado
                </label>
                <SearchBar placeholder="Nombre del empleado..." onSearch={setSearchTerm} className="w-full" data-id="element-2037" />
              </div>
            </div>

            {/* Active filters */}
            {hasActiveFilters && <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100" data-id="element-2038">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2039">
                  Activos:
                </span>
                {quickFilter !== 'Este Mes' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-900 text-white rounded-md text-[11px] font-medium" data-id="element-2040">
                    <Calendar size={10} data-id="element-2041" /> {getDateLabel()}
                  </span>}
                {searchTerm && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-900 text-white rounded-md text-[11px] font-medium" data-id="element-2042">
                    <Search size={10} data-id="element-2043" /> &quot;{searchTerm}&quot;
                  </span>}
                <button onClick={clearFilters} className="text-[11px] text-gray-500 hover:text-red-600 font-medium underline underline-offset-2 transition-colors ml-1" data-id="element-2044">
                  Limpiar todo
                </button>
              </div>}
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-id="element-2045">
        <Card className="p-4 border-l-4 border-l-blue-500" data-id="element-2046">
          <div className="flex items-start justify-between gap-2" data-id="element-2047">
            <div className="min-w-0" data-id="element-2048">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1" data-id="element-2049">
                Total Pedidos
              </p>
              <p className="text-2xl font-bold text-gray-900 leading-none tabular-nums" data-id="element-2050">
                {summary.totalOrders}
              </p>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg shrink-0 hidden sm:flex" data-id="element-2051">
              <ShoppingBag className="w-4 h-4 text-blue-600" data-id="element-2052" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500" data-id="element-2053">
          <div className="flex items-start justify-between gap-2" data-id="element-2054">
            <div className="min-w-0" data-id="element-2055">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1" data-id="element-2056">
                Consumo Total
              </p>
              <p className="text-sm font-bold text-gray-900 leading-tight tabular-nums whitespace-nowrap" data-id="element-2057">
                {formatCurrency(summary.totalConsumption)}
              </p>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg shrink-0 hidden sm:flex" data-id="element-2058">
              <DollarSign className="w-4 h-4 text-emerald-600" data-id="element-2059" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500" data-id="element-2060">
          <div className="flex items-start justify-between gap-2" data-id="element-2061">
            <div className="min-w-0" data-id="element-2062">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1" data-id="element-2063">
                Empleados Activos
              </p>
              <p className="text-2xl font-bold text-gray-900 leading-none tabular-nums" data-id="element-2064">
                {summary.activeEmployees}
              </p>
            </div>
            <div className="p-2 bg-purple-50 rounded-lg shrink-0 hidden sm:flex" data-id="element-2065">
              <Users className="w-4 h-4 text-purple-600" data-id="element-2066" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-indigo-500" data-id="element-2067">
          <div className="flex items-start justify-between gap-2" data-id="element-2068">
            <div className="min-w-0" data-id="element-2069">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1" data-id="element-2070">
                Promedio / Emp.
              </p>
              <p className="text-sm font-bold text-gray-900 leading-tight tabular-nums whitespace-nowrap" data-id="element-2071">
                {formatCurrency(summary.avgPerEmployee)}
              </p>
            </div>
            <div className="p-2 bg-indigo-50 rounded-lg shrink-0 hidden sm:flex" data-id="element-2072">
              <TrendingUp className="w-4 h-4 text-indigo-600" data-id="element-2073" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="overflow-hidden border border-gray-200 shadow-sm" noPadding data-id="element-2074">
        {employeeStats.length === 0 ? <div className="text-center py-16 px-4" data-id="element-2075">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2076">
              <Search size={32} className="text-gray-300" data-id="element-2077" />
            </div>
            <h3 className="text-lg font-bold text-gray-900" data-id="element-2078">
              No se encontraron datos
            </h3>
            <p className="text-gray-500 mt-2 text-sm" data-id="element-2079">
              No hay pedidos registrados para el período seleccionado.
            </p>
            {hasActiveFilters && <button onClick={clearFilters} className="mt-4 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors" data-id="element-2080">
                Limpiar filtros
              </button>}
          </div> : <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto" data-id="element-2081">
              <table className="w-full text-left border-collapse" data-id="element-2082">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2083">
                  <tr data-id="element-2084">
                    <th className="pl-6 pr-2 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider w-12 text-center" data-id="element-2085">
                      #
                    </th>
                    <th className="px-2 py-3.5 w-8" data-id="element-2086"></th>
                    <th className="px-4 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-2087">
                      Empleado
                    </th>
                    <th className="px-4 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-2088">
                      Sucursal
                    </th>
                    <th className="px-4 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center" data-id="element-2089">
                      Pedidos
                    </th>
                    <th className="px-4 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right" style={{
                  minWidth: '180px'
                }} data-id="element-2090">
                      Total Consumo
                    </th>
                    <th className="px-4 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right" data-id="element-2091">
                      Promedio
                    </th>
                    <th className="pl-4 pr-6 py-3.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-right" data-id="element-2092">
                      Último Pedido
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white" data-id="element-2093">
                  {employeeStats.map((stat, index) => {
                const isExpanded = expandedEmployee === stat.name;
                const avgOrder = stat.totalSpent / stat.orders.length;
                const percentage = maxConsumption > 0 ? stat.totalSpent / maxConsumption * 100 : 0;
                return <Fragment key={stat.name} data-id="element-2094">
                        <tr onClick={() => toggleExpand(stat.name)} className={`cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/60' : 'hover:bg-gray-50/80'}`} data-id="element-2095">
                          <td className="pl-6 pr-2 py-3.5 text-center" data-id="element-2096">
                            <span className="font-mono text-xs text-gray-400 font-bold" data-id="element-2097">
                              {index + 1}
                            </span>
                          </td>
                          <td className="px-2 py-3.5 text-gray-400" data-id="element-2098">
                            {isExpanded ? <ChevronDown size={16} data-id="element-2099" /> : <ChevronRight size={16} data-id="element-2100" />}
                          </td>
                          <td className="px-4 py-3.5" data-id="element-2101">
                            <span className="font-semibold text-gray-900 text-sm" data-id="element-2102">
                              {stat.name}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-sm text-gray-500" data-id="element-2103">
                            {stat.primaryBranch}
                          </td>
                          <td className="px-4 py-3.5 text-center" data-id="element-2104">
                            <span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700" data-id="element-2105">
                              {stat.orders.length}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right relative overflow-hidden" data-id="element-2106">
                            <div className="absolute inset-y-1 right-0 bg-blue-50 rounded-l transition-all duration-500 ease-out" style={{
                        width: `${percentage}%`
                      }} data-id="element-2107" />
                            <span className="relative z-10 font-bold text-gray-900 text-sm tabular-nums" data-id="element-2108">
                              {formatCurrency(stat.totalSpent)}
                            </span>
                          </td>
                          <td className="px-4 py-3.5 text-right text-sm text-gray-500 tabular-nums" data-id="element-2109">
                            {formatCurrency(avgOrder)}
                          </td>
                          <td className="pl-4 pr-6 py-3.5 text-right text-xs text-gray-400" data-id="element-2110">
                            {stat.lastOrderDate.toLocaleDateString('es-DO', {
                        day: 'numeric',
                        month: 'short'
                      })}
                          </td>
                        </tr>
                        {isExpanded && <tr data-id="element-2111">
                            <td colSpan={8} className="p-0" data-id="element-2112">
                              <div className="bg-gray-50 px-6 py-4" data-id="element-2113">
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2114">
                                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-2115">
                                    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-2116">
                                      Historial de Pedidos
                                    </span>
                                    <span className="text-xs text-gray-400" data-id="element-2117">
                                      {stat.orders.length} órdenes
                                    </span>
                                  </div>
                                  <div className="max-h-[360px] overflow-y-auto" data-id="element-2118">
                                    <table className="w-full text-sm" data-id="element-2119">
                                      <thead className="bg-gray-50/80 border-b border-gray-100 sticky top-0 z-10" data-id="element-2120">
                                        <tr data-id="element-2121">
                                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2122">
                                            Orden
                                          </th>
                                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2123">
                                            Fecha
                                          </th>
                                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2124">
                                            Detalle
                                          </th>
                                          <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2125">
                                            Sucursal
                                          </th>
                                          <th className="px-4 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2126">
                                            Estado
                                          </th>
                                          <th className="px-4 py-2 text-right text-[10px] font-semibold text-gray-500 uppercase" data-id="element-2127">
                                            Monto
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-50" data-id="element-2128">
                                        {stat.orders.sort((a, b) => new Date(b.orderDateISO || b.orderDate).getTime() - new Date(a.orderDateISO || a.orderDate).getTime()).map(order => <tr key={order.id} className="hover:bg-gray-50/50" data-id="element-2129">
                                              <td className="px-4 py-2.5 font-mono text-xs text-gray-500" data-id="element-2130">
                                                #{order.orderNo}
                                              </td>
                                              <td className="px-4 py-2.5 text-xs text-gray-500" data-id="element-2131">
                                                {new Date(order.orderDateISO || order.orderDate).toLocaleString('es-DO', {
                                      day: 'numeric',
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                              </td>
                                              <td className="px-4 py-2.5 text-gray-800 text-sm" data-id="element-2132">
                                                {displayProductName(order.product)}
                                              </td>
                                              <td className="px-4 py-2.5 text-xs text-gray-400" data-id="element-2133">
                                                {order.branch}
                                              </td>
                                              <td className="px-4 py-2.5 text-center" data-id="element-2134">
                                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full ${order.status === 'Entregado' ? 'bg-green-100 text-green-700' : order.status === 'Listo' ? 'bg-blue-100 text-blue-700' : order.status === 'En Preparación' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-2135">
                                                  {order.status}
                                                </span>
                                              </td>
                                              <td className="px-4 py-2.5 text-right font-medium text-sm tabular-nums" data-id="element-2136">
                                                {formatCurrency(order.total)}
                                              </td>
                                            </tr>)}
                                      </tbody>
                                      <tfoot className="bg-gray-50 border-t border-gray-200 sticky bottom-0" data-id="element-2137">
                                        <tr data-id="element-2138">
                                          <td colSpan={5} className="px-4 py-2.5 text-right text-xs font-bold text-gray-500 uppercase" data-id="element-2139">
                                            Total
                                          </td>
                                          <td className="px-4 py-2.5 text-right font-bold text-gray-900 tabular-nums" data-id="element-2140">
                                            {formatCurrency(stat.totalSpent)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden p-3 space-y-3" data-id="element-2141">
              {employeeStats.map((stat, index) => {
            const isExpanded = expandedEmployee === stat.name;
            const percentage = maxConsumption > 0 ? stat.totalSpent / maxConsumption * 100 : 0;
            return <div key={stat.name} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2142">
                    <div onClick={() => toggleExpand(stat.name)} className="p-4 cursor-pointer active:bg-gray-50" data-id="element-2143">
                      <div className="flex justify-between items-start mb-3" data-id="element-2144">
                        <div className="flex items-center gap-3 min-w-0" data-id="element-2145">
                          <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center font-bold text-white text-xs shrink-0" data-id="element-2146">
                            {index + 1}
                          </div>
                          <div className="min-w-0" data-id="element-2147">
                            <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-2148">
                              {stat.name}
                            </h3>
                            <div className="flex items-center text-xs text-gray-400 gap-1" data-id="element-2149">
                              <Building size={10} data-id="element-2150" />
                              <span className="truncate" data-id="element-2151">
                                {stat.primaryBranch}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-3" data-id="element-2152">
                          <p className="font-bold text-gray-900" data-id="element-2153">
                            {formatCurrency(stat.totalSpent)}
                          </p>
                          <p className="text-[10px] text-gray-400 font-medium" data-id="element-2154">
                            {stat.orders.length} pedidos
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1 mb-3" data-id="element-2155">
                        <div className="bg-blue-500 h-1 rounded-full transition-all duration-500" style={{
                    width: `${percentage}%`
                  }} data-id="element-2156" />
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-400" data-id="element-2157">
                        <span data-id="element-2158">
                          Último:{' '}
                          {stat.lastOrderDate.toLocaleDateString('es-DO', {
                      day: 'numeric',
                      month: 'short'
                    })}
                        </span>
                        <div className="flex items-center gap-1 text-blue-600 font-medium" data-id="element-2159">
                          {isExpanded ? 'Ocultar' : 'Ver detalles'}
                          {isExpanded ? <ChevronUp size={14} data-id="element-2160" /> : <ChevronDown size={14} data-id="element-2161" />}
                        </div>
                      </div>
                    </div>
                    {isExpanded && <div className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-2" data-id="element-2162">
                        {stat.orders.sort((a, b) => new Date(b.orderDateISO || b.orderDate).getTime() - new Date(a.orderDateISO || a.orderDate).getTime()).map(order => <div key={order.id} className="bg-white p-3 rounded-lg border border-gray-100 text-sm" data-id="element-2163">
                              <div className="flex justify-between items-center mb-1.5" data-id="element-2164">
                                <span className="font-mono text-[10px] text-gray-400" data-id="element-2165">
                                  #{order.orderNo}
                                </span>
                                <span className="text-[10px] text-gray-400" data-id="element-2166">
                                  {new Date(order.orderDateISO || order.orderDate).toLocaleDateString('es-DO', {
                        day: 'numeric',
                        month: 'short'
                      })}
                                </span>
                              </div>
                              <div className="font-medium text-gray-900 text-sm mb-2" data-id="element-2167">
                                {displayProductName(order.product)}
                              </div>
                              <div className="flex justify-between items-center" data-id="element-2168">
                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full ${order.status === 'Entregado' ? 'bg-green-100 text-green-700' : order.status === 'Listo' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-2169">
                                  {order.status}
                                </span>
                                <span className="font-bold text-gray-900 text-sm tabular-nums" data-id="element-2170">
                                  {formatCurrency(order.total)}
                                </span>
                              </div>
                            </div>)}
                        <div className="flex justify-between items-center pt-2 px-1 border-t border-gray-200" data-id="element-2171">
                          <span className="text-xs font-bold text-gray-500 uppercase" data-id="element-2172">
                            Total
                          </span>
                          <span className="font-bold text-gray-900" data-id="element-2173">
                            {formatCurrency(stat.totalSpent)}
                          </span>
                        </div>
                      </div>}
                  </div>;
          })}
            </div>
          </>}
      </Card>
    </div>;
}